import { graphHooks } from '@tunasong/graph-lib/react'
import { applyBandPin, applyBandUnpin, isBandPinned, PIN_CAP, restoreBandPin, sortedBandPins } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import type { BandPin, BandSpace } from '@tunasong/schemas'
import type { FC } from 'react'
import { useEffect, useEffectEvent, useRef } from 'react'
import { useActionToast } from '../alert/use-action-toast.js'
import { bandLabel, bandPinCopy } from './band-pin-copy.js'
import { useBandPinRequest } from './band-pin-store.js'

/**
 * Applies band pin requests (GH #2822): writes the band's pins, says what happened, and offers Undo for an unpin,
 * which changes what every member sees. Mounted once, so the toasts outlive the menu that asked.
 *
 * Two members can pin at once: the save is conditional, and a 412 re-applies this pin to the pins made since
 * (`mergeBandPins`). If that leaves no room, the pin is dropped there; the watch below then says the shelf is full,
 * so the pin is never lost silently.
 */
export const BandPinHost: FC = () => {
  const request = useBandPinRequest()
  const { entity: band } = graphHooks.useEntity<BandSpace>(request?.bandId)
  const updateEntity = graphHooks.useEntityUpdate<BandSpace>()
  const { userId } = useCurrentUser()
  const { showToast, toast } = useActionToast()
  /** A request made before this host mounted is history, not a new one: a remount must not replay it */
  const handled = useRef(request?.seq ?? 0)
  /**
   * A pin sent, and the band's version it was sent on. The optimistic cache entry keeps that version's `updatedAt`,
   * so only a newer one is the server's answer.
   */
  const pending = useRef<{ bandId: string; entityId: string; sentOn?: string } | null>(null)

  const writePins = (bandId: string, pins: BandPin[]) => updateEntity(bandId, { pins })

  const handle = useEffectEvent(
    (current: NonNullable<typeof request>, target: BandSpace & { id: string; updatedAt?: string }) => {
      const name = bandLabel(target)
      if (current.action === 'pin') {
        const { pins, outcome } = applyBandPin(target.pins, { entityId: current.entityId, pinnedBy: userId ?? '' })
        if (outcome === 'full') {
          showToast({ message: bandPinCopy.full(name) })
          return
        }
        if (outcome === 'pinned') {
          pending.current = { bandId: target.id, entityId: current.entityId, sentOn: target.updatedAt }
          writePins(target.id, pins)
          showToast({ message: bandPinCopy.pinned(name) })
        }
        return
      }
      const removed = sortedBandPins(target.pins).find(pin => pin.entityId === current.entityId)
      if (!removed) {
        return
      }
      writePins(target.id, applyBandUnpin(target.pins, current.entityId))
      showToast({
        message: bandPinCopy.unpinned(name),
        actionLabel: 'Undo',
        duration: 6000,
        onAction: () => {
          /** Undo builds on the band as it is now, so a pin made meanwhile is kept */
          const latest = bandRef.current?.id === target.id ? bandRef.current : target
          writePins(target.id, restoreBandPin(applyBandUnpin(latest.pins, current.entityId), removed))
        },
      })
    }
  )

  const bandRef = useRef<(BandSpace & { id: string }) | undefined>(undefined)
  useEffect(() => {
    bandRef.current = band
  }, [band])

  useEffect(() => {
    if (!request || !band || band.id !== request.bandId || handled.current === request.seq) {
      return
    }
    handled.current = request.seq
    handle(request, band)
  }, [request, band])

  /** The pin met a newer version, and the merge found the shelf full: say so rather than lose it */
  const watchPending = useEffectEvent((current: BandSpace & { id: string; updatedAt?: string }) => {
    const sent = pending.current
    if (!sent || sent.bandId !== current.id || current.updatedAt === sent.sentOn) {
      return
    }
    if (isBandPinned(current.pins, sent.entityId)) {
      pending.current = null
      return
    }
    if ((current.pins ?? []).length >= PIN_CAP) {
      pending.current = null
      showToast({ message: bandPinCopy.full(bandLabel(current)) })
    }
  })
  useEffect(() => {
    if (band) {
      watchPending(band)
    }
  }, [band])

  return toast
}
